import { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Typography, message } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { providerApi } from '../../api/businessApi';
import { useBusinessStore } from '../../store/businessStore';
import { telegram } from '../../utils/telegram';
import ProviderRow from './ProviderRow';
import ConfirmSheet from '../provider/shared/ConfirmSheet';
import type { Provider } from '../../types/business';

const { Text } = Typography;

export interface ProvidersListSectionHandle {
  refresh: () => Promise<void>;
}

const ProvidersListSection = forwardRef<ProvidersListSectionHandle, object>(
  function ProvidersListSection(_props, ref) {
    const { t, i18n } = useTranslation('owner');
    const { selectedBusiness } = useBusinessStore();
    const tenantId = selectedBusiness?.id;

    const [providers, setProviders] = useState<Provider[]>([]);
    const [deleteTarget, setDeleteTarget] = useState<Provider | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [blockingId, setBlockingId] = useState<number | null>(null);

    const loadProviders = useCallback(async () => {
      if (!tenantId) return;
      try {
        const data = await providerApi.getAll(tenantId);
        setProviders(data);
      } catch {
        void message.error(t('providers.loadError'));
      }
    }, [tenantId, t]);

    useImperativeHandle(ref, () => ({ refresh: loadProviders }), [loadProviders]);

    useEffect(() => {
      void loadProviders();
    }, [loadProviders]);

    useEffect(() => {
      const handler = () => { void loadProviders(); };
      i18n.on('languageChanged', handler);
      return () => { i18n.off('languageChanged', handler); };
    }, [i18n, loadProviders]);

    if (!tenantId) return null;

    const handleToggle = async (provider: Provider) => {
      // Optimistic
      const next = !provider.active;
      setProviders((prev) => prev.map((p) => (p.id === provider.id ? { ...p, active: next } : p)));
      telegram.hapticFeedback.impact('light');
      try {
        const updated = provider.active
          ? await providerApi.deactivate(tenantId, provider.id)
          : await providerApi.activate(tenantId, provider.id);
        setProviders((prev) => prev.map((p) => (p.id === provider.id ? updated : p)));
      } catch (err) {
        // Revert
        setProviders((prev) => prev.map((p) => (p.id === provider.id ? provider : p)));
        // Backend xato sababini Accept-Language bo'yicha tarjimalab message'da
        // qaytaradi — uni to'g'ridan-to'g'ri ko'rsatamiz. message bo'lmasa
        // (tarmoq xatosi va h.k.) umumiy matnga tushamiz.
        const beMessage = (err as { response?: { data?: { message?: string } } })
          ?.response?.data?.message;
        void message.error(beMessage ?? t('providers.toggleError'));
      }
    };

    const handleBlockToggle = async (provider: Provider) => {
      const isBlocked = !provider.enabledByOwner;
      const confirmed = await telegram.showConfirm(
        isBlocked
          ? t('providers.unblockConfirm', { name: provider.displayName })
          : t('providers.blockConfirm', { name: provider.displayName }),
      );
      if (!confirmed) return;
      setBlockingId(provider.id);
      try {
        const updated = isBlocked
          ? await providerApi.unblock(tenantId, provider.id)
          : await providerApi.block(tenantId, provider.id);
        setProviders((prev) => prev.map((p) => (p.id === provider.id ? updated : p)));
        telegram.hapticFeedback.notification('success');
        void message.success(
          isBlocked
            ? t('providers.unblockedMsg', { name: provider.displayName })
            : t('providers.blockedMsg', { name: provider.displayName }),
        );
      } catch {
        void message.error(isBlocked ? t('providers.unblockError') : t('providers.blockError'));
        telegram.hapticFeedback.notification('error');
      } finally {
        setBlockingId(null);
      }
    };

    const handleDeleteConfirm = async () => {
      if (!deleteTarget) return;
      const target = deleteTarget;
      setDeleting(true);
      try {
        await providerApi.remove(tenantId, target.id);
        setProviders((prev) => prev.filter((p) => p.id !== target.id));
        telegram.hapticFeedback.notification('success');
        void message.success(t('providers.removedMsg'));
        setDeleteTarget(null);
      } catch {
        void message.error(t('providers.removeError'));
        telegram.hapticFeedback.notification('error');
      } finally {
        setDeleting(false);
      }
    };

    const activeCount = providers.filter((p) => p.active).length;

    return (
      <>
        {/* Section header */}
        <div
          className="bl-animate-in bl-animate-in-delay-2"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 4,
            paddingLeft: 4,
            paddingRight: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TeamOutlined style={{ fontSize: 11, color: 'var(--bl-text-muted, #9ca3af)' }} />
            <Text
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--bl-text-muted, #9ca3af)',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              {t('providers.title')}
            </Text>
          </div>

          {providers.length > 0 && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--bl-accent, #3390ec)',
                background: 'var(--bl-accent-soft, rgba(51,144,236,0.10))',
                border: '1px solid rgba(51,144,236,0.20)',
                borderRadius: 999,
                padding: '2px 9px',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {activeCount} / {providers.length}
            </span>
          )}
        </div>

        {/* List */}
        {providers.length === 0 ? (
          <EmptyState />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {providers.map((provider, idx) => {
              const delayClass =
                idx < 2 ? 'bl-animate-in bl-animate-in-delay-2' :
                idx < 4 ? 'bl-animate-in bl-animate-in-delay-3' :
                'bl-animate-in bl-animate-in-delay-3';
              return (
                <div key={provider.id} className={delayClass}>
                  <ProviderRow
                    provider={provider}
                    busy={blockingId === provider.id}
                    onToggle={(p) => void handleToggle(p)}
                    onBlockToggle={(p) => void handleBlockToggle(p)}
                    onDelete={(p) => setDeleteTarget(p)}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Delete confirm sheet */}
        <ConfirmSheet
          open={deleteTarget !== null}
          title={t('providers.deleteSheetTitle')}
          description={
            deleteTarget
              ? `${deleteTarget.displayName} — ${t('providers.deleteSheetDescPrefix')}`
              : ''
          }
          confirmText={t('providers.deleteConfirm')}
          cancelText={t('providers.deleteCancel')}
          danger
          loading={deleting}
          onConfirm={() => void handleDeleteConfirm()}
          onCancel={() => !deleting && setDeleteTarget(null)}
        />
      </>
    );
  },
);

export default ProvidersListSection;

// ─── Empty state ────────────────────────────────────────────────────────────

function EmptyState() {
  const { t } = useTranslation('owner');
  return (
    <div
      className="bl-animate-in bl-animate-in-delay-2"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 24px 24px',
        textAlign: 'center',
        background: 'var(--bl-surface, #fff)',
        border: '1px dashed var(--bl-divider, #ebebeb)',
        borderRadius: 14,
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background:
            'linear-gradient(135deg, rgba(51,144,236,0.10) 0%, rgba(51,144,236,0.24) 100%)',
          border: '1px solid rgba(51,144,236,0.20)',
          color: 'var(--bl-accent, #3390ec)',
          fontSize: 24,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 12,
        }}
      >
        <TeamOutlined />
      </div>
      <Text
        style={{
          fontSize: 13,
          color: 'var(--bl-text-muted, #6b7280)',
          lineHeight: 1.45,
        }}
      >
        {t('providers.empty')}
      </Text>
    </div>
  );
}
